let rootSheet = null
const rootThemeVars = {}

const THEME_VAR_VALUE = /^([#][0-9a-fA-F]{3,8}|rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)|rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)|[a-zA-Z]+|inherit|initial|unset|none|auto|\d+(\.\d+)?(%|px|rem|em|vh|vw|deg|fr)?|repeat\(\d+,\s*[\w%.]+\)|[\d.]+%\s+[\d.]+%)$/

function sanitizeThemeVarValue (value) {
  if (value == null || value === '') return ''
  const v = String(value).trim()
  if (!THEME_VAR_VALUE.test(v)) return ''
  return v
}

/** Values from app code (not raw user input): block injection, allow normal CSS lengths/keywords. */
function sanitizeCssValue (value) {
  if (value == null || value === '') return ''
  const v = String(value).trim()
  if (/[;{}]/.test(v) || /url\s*\(/i.test(v)) return ''
  return v
}

function getOrCreateRootSheet () {
  if (rootSheet) return rootSheet
  rootSheet = new CSSStyleSheet()
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, rootSheet]
  return rootSheet
}

let dynamicElementSheet = null
const dynamicRulesById = new Map()

function syncDynamicElementSheet () {
  if (!dynamicElementSheet) {
    dynamicElementSheet = new CSSStyleSheet()
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, dynamicElementSheet]
  }
  const blocks = []
  for (const [id, declarations] of dynamicRulesById) {
    const body = Object.entries(declarations)
      .map(([prop, val]) => {
        const safe = sanitizeCssValue(val)
        if (!safe) return ''
        const kebab = prop.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)
        return `${kebab}:${safe}`
      })
      .filter(Boolean)
      .join(';')
    if (body) {
      const escapedId = id.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
      blocks.push(`[data-runtime-style-id="${escapedId}"]{${body}}`)
    }
  }
  dynamicElementSheet.replaceSync(blocks.length ? blocks.join('\n') : ':root{}')
}

/**
 * CSP-safe theme variables on :root (constructable stylesheet).
 * Values are merged so callers cannot wipe previously set planning/meeting colors.
 * @param {Record<string, string>} vars map of name without -- prefix to value
 */
export function setRootThemeVars (vars) {
  if (!vars || typeof vars !== 'object') return
  Object.entries(vars).forEach(([key, value]) => {
    const safe = sanitizeThemeVarValue(value)
    if (!safe) return
    const name = key.startsWith('--') ? key.slice(2) : key
    rootThemeVars[name] = safe
  })
  const parts = Object.entries(rootThemeVars).map(([key, value]) => `--${key}:${value}`)
  getOrCreateRootSheet().replaceSync(parts.length ? `:root{${parts.join(';')}}` : ':root{}')
}

function ensureRuntimeStyleId (element) {
  if (!element.dataset.runtimeStyleId) {
    element.dataset.runtimeStyleId = `rt-${Math.random().toString(36).slice(2, 11)}`
  }
  return element.dataset.runtimeStyleId
}

function resolveElements (element) {
  if (element == null) return []
  if (element.nodeType === Node.ELEMENT_NODE) return [element]
  if (typeof element.length === 'number') {
    const nodes = []
    for (let i = 0; i < element.length; i++) {
      const node = element[i]
      if (node?.nodeType === Node.ELEMENT_NODE) nodes.push(node)
    }
    return nodes
  }
  return []
}

/** Drop dynamic rules for elements under a subtree (e.g. before recomputing video grid). */
export function clearDynamicStylesInSubtree (root) {
  const roots = resolveElements(root)
  for (const rootEl of roots) {
    if (rootEl.dataset?.runtimeStyleId) {
      dynamicRulesById.delete(rootEl.dataset.runtimeStyleId)
    }
    rootEl.querySelectorAll('[data-runtime-style-id]').forEach((el) => {
      if (el.dataset.runtimeStyleId) dynamicRulesById.delete(el.dataset.runtimeStyleId)
    })
  }
  syncDynamicElementSheet()
}

/** Apply declarations to a DOM node or jQuery collection (same as .css() on all matches). */
export function applyElementStyles (element, declarations) {
  const targets = resolveElements(element)
  for (const el of targets) {
    const id = ensureRuntimeStyleId(el)
    dynamicRulesById.set(id, { ...dynamicRulesById.get(id), ...declarations })
  }
  syncDynamicElementSheet()
}

export function clearElementSheet (element) {
  const targets = resolveElements(element)
  for (const el of targets) {
    const id = el.dataset?.runtimeStyleId
    if (id) dynamicRulesById.delete(id)
  }
  syncDynamicElementSheet()
}

function isWhiteColor (value) {
  if (!value) return false
  const v = String(value).trim().toLowerCase()
  if (v === 'white') return true
  if (v === '#fff' || v === '#ffff' || v === '#ffffff') return true
  const rgb = v.replace(/\s+/g, '')
  if (rgb === 'rgb(255,255,255)') return true
  if (rgb === 'rgba(255,255,255,1)' || rgb === 'rgba(255,255,255,1.0)') return true
  return false
}

function sanitizeFontFamily (value) {
  if (value == null || value === '') return ''
  const v = String(value).trim()
  if (!/^[\w\s"',.-]+$/.test(v)) return ''
  return v
}

let meetingLayoutSheet = null

/** CSP-safe meeting layout rules (document-level sheet; IDs match pre-refactor jQuery targets). */
export function setMeetingLayoutRules (cssRules) {
  if (!cssRules) return
  if (!meetingLayoutSheet) {
    meetingLayoutSheet = new CSSStyleSheet()
    document.adoptedStyleSheets = [...document.adoptedStyleSheets, meetingLayoutSheet]
  }
  meetingLayoutSheet.replaceSync(cssRules)
}

export function syncMeetingSiteTheme (site) {
  if (!site) return
  const raw = site.videochat_text_color ? String(site.videochat_text_color).trim() : ''
  const videochatTextColor = (!raw || isWhiteColor(raw)) ? '#073C5A' : raw
  const vars = {
    'videochat-text-color': videochatTextColor
  }
  if (site.overlay_text_color) {
    vars['overlay-text-color'] = site.overlay_text_color
  }
  if (site.waitingroom_font_color) {
    vars['waitingroom-font-color'] = site.waitingroom_font_color
  }
  const font = sanitizeFontFamily(site.videochat_font_type)
  if (font) {
    vars['videochat-font-family'] = font
  }
  setRootThemeVars(vars)
}
